import { memo } from 'react'
import {
  OutlinedAdornment,
  OutlinedAddon,
  FormControlLabel,
  Checkbox,
  Link,
  Typography,
} from '@maas-components/core'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { useFormikContext } from 'formik'
import i18n from 'core/common/i18n'
import { isNumber } from 'core/common/utils/is'
import { getErrorMessage, validateField } from 'core/common/utils/validation'
import TextField from 'core/components/TextField'
import { URL_MAGALU_FULFILLMENT } from '../../constants'
import { TStockChannels, TStockInitialValues } from '../../types'
import ChannelName from '../ChannelName'
import { StyledCard, LightTooltip } from './style'

type IStockChannel = {
  name: string
  channel: TStockChannels
  isMultiChannel: boolean
  handleInputChange: (
    channel: TStockChannels,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void
  variationIndex?: number
  fulfillmentLink?: string
}

const StockChannel = ({
  name,
  channel,
  isMultiChannel,
  handleInputChange,
  variationIndex,
  fulfillmentLink,
}: IStockChannel) => {
  const { values, errors, touched, submitCount, handleChange } =
    useFormikContext<TStockInitialValues>()
  const isVariation = isNumber(variationIndex)
  const fulfillmentName = isVariation ? `variations.${variationIndex}.fulfillment` : 'fulfillment'

  let stockQuantityValue = values.stocks?.[0]?.quantity || 0

  if (isVariation && values.variations?.[variationIndex as number]?.stocks?.length > 0) {
    stockQuantityValue = values.variations[variationIndex as number].stocks[0]?.quantity || 0
  }

  return (
    <StyledCard variant="outlined">
      {isMultiChannel ? <ChannelName name={name} /> : null}
      <OutlinedAdornment
        position="end"
        error={validateField('stocks[0].quantity', errors, touched, submitCount > 0)}
      >
        <TextField
          type="number"
          variant="outlined"
          label={i18n.t('productNew.stock.input.placeholder')}
          name="stocks[0].quantity"
          value={stockQuantityValue.toString()}
          InputProps={{
            inputProps: { min: 0, max: 9999999 },
          }}
          onKeyDown={event => {
            if (event.key === '.' || event.key === ',') {
              event.preventDefault()
            }
          }}
          onChange={event => handleInputChange(channel, event)}
          style={{ backgroundColor: '#fff', width: '100%' }}
          error={validateField('stocks[0].quantity', errors, touched, submitCount > 0)}
          helperText={getErrorMessage('stocks[0].quantity', errors, touched)}
        />

        <OutlinedAddon position="end">Un.</OutlinedAddon>
      </OutlinedAdornment>
      <div className="d-flex align-center">
        <FormControlLabel
          control={<Checkbox />}
          label={i18n.t('productNew.stock.stockMagalu')}
          onChange={handleChange}
          className="mr-1"
          checked={
            isVariation
              ? Boolean(values?.variations?.[variationIndex as number]?.fulfillment)
              : Boolean(values?.fulfillment)
          }
          name={fulfillmentName}
        />

        <LightTooltip
          title={
            <>
              <Typography variant="body2" style={{ pointerEvents: 'auto' }}>
                É o seu produto armazenado em um CD do Magalu. Ao marcar esta opção o produto será
                enviado para aprovação. &nbsp;
                <Link
                  target="_blank"
                  href={fulfillmentLink ?? URL_MAGALU_FULFILLMENT}
                  style={{ textDecoration: 'underline', fontWeight: 500 }}
                >
                  Saiba mais
                </Link>
              </Typography>
            </>
          }
          placement="right"
          leaveDelay={1500}
        >
          <HelpOutlineIcon style={{ marginLeft: 4, fontSize: 16 }} color="primary" />
        </LightTooltip>
      </div>
    </StyledCard>
  )
}

export default memo(StockChannel)
